// src/app/shared/ui/LoadingLottieIcon.tsx
import { useEffect, useRef } from "react";
import Lottie, {LottieRefCurrentProps } from "lottie-react"; 
import loadingAnimation from "@/assets/lottie/ic_loading.json";

export type LoadingLottieMode = "loop" | "tail" | "stop";

interface LoadingLottieIconProps {
  mode: LoadingLottieMode;
  isError?: boolean;
  onTailComplete?: () => void;
  sizeClassName?: string;
}

export function LoadingLottieIcon({
  mode,
  isError = false,
  onTailComplete,
  sizeClassName = "h-24 w-24",
}: LoadingLottieIconProps) {
  const lottieRef = useRef<LottieRefCurrentProps>(null);
  const playbackStateRef = useRef<LoadingLottieMode>("stop");

  const getAnimationItem = () => {
    return (lottieRef.current as unknown as { animationItem?: any })?.animationItem;
  };

  const getFrameMeta = () => {
    const animationItem = getAnimationItem();
    if (!animationItem) {
      return null;
    }
    const totalFrames = Math.max(1, Math.floor(animationItem.getDuration(true)));
    const lastFrame = totalFrames - 1;
    const currentFrame = Math.max(0, Math.floor(animationItem.currentFrame ?? 0));
    return { animationItem, totalFrames, lastFrame, currentFrame };
  };

  useEffect(() => {
    lottieRef.current?.setSubframe(true);
    lottieRef.current?.setSpeed(mode === "tail" ? 1.15 : 1.05);
  }, [mode]);

  useEffect(() => {
    const frameMeta = getFrameMeta();
    if (!frameMeta) {
      return;
    }

    const { animationItem, lastFrame, currentFrame } = frameMeta;
    if (mode === "loop") {
      if (playbackStateRef.current === "loop") {
        return;
      }
      animationItem.loop = true;
      animationItem.play();
      playbackStateRef.current = "loop";
      return;
    }

    if (mode === "tail") {
      if (playbackStateRef.current === "tail" || playbackStateRef.current === "stop") {
        return;
      }

      animationItem.loop = false;
      if (currentFrame >= lastFrame) {
        animationItem.goToAndStop(lastFrame, true);
        playbackStateRef.current = "stop";
        onTailComplete?.();
        return;
      }
      playbackStateRef.current = "tail";
      animationItem.playSegments([currentFrame, lastFrame], true);
      return;
    }

    if (mode === "stop") {
      animationItem.loop = false;
      animationItem.pause();
      playbackStateRef.current = "stop";
    }
  }, [mode, onTailComplete]);

  return (
    <div
      className={`relative z-10 overflow-hidden rounded-full transition-all duration-500 ${sizeClassName} ${
        isError ? "opacity-85 grayscale-[0.25]" : ""
      }`}
    >
      <Lottie
        lottieRef={lottieRef}
        animationData={loadingAnimation}
        loop={false}
        autoplay={false}
        onDOMLoaded={() => {
          if (mode === "loop") {
            const frameMeta = getFrameMeta();
            if (!frameMeta) {
              return;
            }
            frameMeta.animationItem.loop = true;
            frameMeta.animationItem.play();
            playbackStateRef.current = "loop";
          } else if (mode === "stop") {
            const frameMeta = getFrameMeta();
            if (!frameMeta) {
              return;
            }
            frameMeta.animationItem.loop = false;
            frameMeta.animationItem.pause();
            playbackStateRef.current = "stop";
          }
        }}
        onComplete={() => {
          const frameMeta = getFrameMeta();
          if (!frameMeta) {
            return;
          }
          const { animationItem, lastFrame } = frameMeta;

          if (playbackStateRef.current === "tail") {
            animationItem.goToAndStop(lastFrame, true);
            playbackStateRef.current = "stop";
            onTailComplete?.();
            return;
          }

          if (playbackStateRef.current === "loop") {
            animationItem.playSegments([0, lastFrame], true);
          }
        }}
        className="h-full w-full"
        renderer="svg"
      />
    </div>
  );
}